import React, { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { Button } from 'shop/components'
import {
  useShop,
  useModal,
  useConsumerCart,
  useQueryParams,
  useCartMatch,
  useCreateDefaultCart
} from 'shop/hooks'
import Spinner from 'shop/components/Loader/Spinner'
import { CartPayload } from 'shop/components/ProductInfo/types'
import {
  createModifiersCartPayload,
  flattenModifierItemsV2,
  formatModifiersParams
} from '../utils'
import { MdCheck as CheckIcon } from 'react-icons/md'
import { priceWithVat } from 'shop/components/Product/utils'
import { SelectedModifier } from 'shop/components/Landing/types'
import defaultTheme from 'shop/theme/defaultTheme'

import {
  TrackableEvent,
  trackUserActionsFBPixel,
  trackUserActionsGA4
} from 'tracker'
import {
  merchantGA4EcommTrackAddToCart,
  slerpGA4EcommTrackAddToCart
} from 'tracker/GA/ecommerce'
import { FormattedCartItemsTrackParams } from 'tracker/GA/types'
import { CartMutationResponse, CartProductError } from 'shop/types/cart'
import {
  CategoryProduct,
  CategoryProductVariant,
  ProductModifierGroup
} from 'shop/components/Shop/Categories'
import { FulfillmentType } from 'shop/types'
import { StyledStrikethroughPrice } from '../commonStyles'
import {
  findErrorWarning,
  getCurrentCartId,
  isCartProductError
} from 'shop/utils/cart'

interface Props {
  cartParams: CartPayload | undefined
  setCartParams: (arg0?: CartPayload) => void
  isSelectedVariantInStock: boolean
  isProductInStock: boolean
  modifierGroupsValid: boolean
  optionsValid: boolean
  redirectUser: () => void
  product: CategoryProduct
  modifiers?: ProductModifierGroup[]
  // DONT USE THIS PROP. FOR TESTING LANG E2
  // MockedProvider doesn't support dynamic values and
  // the cart_item_id is auto generated. So i had to
  // put a default value here for testing :(
  testCartItemId?: string
  handleFailedAddToCart: () => void
  setHasFailedAddToCart: (hasFailed: boolean) => void
  selectedVariant?: CategoryProductVariant
  variantQuantity: number
  isStoreAcceptingOrders: boolean
}

const Actions = ({
  cartParams,
  isSelectedVariantInStock,
  isProductInStock,
  modifierGroupsValid,
  optionsValid,
  testCartItemId,
  redirectUser,
  product,
  modifiers,
  handleFailedAddToCart,
  setHasFailedAddToCart,
  selectedVariant,
  isStoreAcceptingOrders
}: Props) => {
  const { cart: consumerCart, createOrderItemConsumerCart } = useConsumerCart()
  const { config, partner, currentPartnerStore } = useShop()
  const { closeModal, openModal, isModalOpen, cartSwitchModal } = useModal()
  const { getQueryParam } = useQueryParams()
  const { isCartMatch } = useCartMatch()

  const queryParamFulfillmentType = getQueryParam(
    'fulfillment_type'
  ).toUpperCase() as FulfillmentType

  const [cartLabel, setCartLabel] = useState<React.ReactNode | string>(
    'Add to cart'
  )

  const cartId = getCurrentCartId(config.domain)

  const formattedPrice = useMemo(() => {
    const format = (price: number) =>
      price.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
    const discountedPrice = format(cartParams?.amount.discounted || 0)
    const basePrice = format(cartParams?.amount.base || 0)
    return { basePrice, discountedPrice }
  }, [cartParams?.amount.discounted, cartParams?.amount.base])

  const addToCartCallBack = () => {
    setHasFailedAddToCart(false)
    setCartLabel(
      <>
        <CheckIcon /> Item added to cart
      </>
    )
    setTimeout(() => {
      redirectUser()
    }, 100)
  }

  const ecommTrackAddToCartCallback = (
    params: FormattedCartItemsTrackParams
  ) => {
    // eccommerce tracking
    const variantName = selectedVariant?.name || product.name
    const trackParams = {
      currency: 'gbp',
      value: params.amount,
      items: [
        {
          item_id: params.variantId,
          item_name: product.name,
          affiliation: params.storeName,
          item_brand: params.partnerName,
          item_variant: variantName || product.name,
          price: priceWithVat(params.variantPrice, params.variantVat),
          quantity: params.quantity || 1
        }
      ]
    }
    slerpGA4EcommTrackAddToCart(trackParams)
    merchantGA4EcommTrackAddToCart(trackParams)
  }

  const trackAddToCartCallback = (
    quantity: number,
    appliedModifiers: SelectedModifier[]
  ) => {
    const variantId = selectedVariant?.id || product.defaultVariantId
    const chosenVariantId = variantId || selectedVariant?.name || product.name
    const modifierItems = flattenModifierItemsV2(modifiers)
    const contents =
      appliedModifiers.length > 0
        ? appliedModifiers.map((modifier) => {
            const { modifier_id } = modifier
            const modifierSku = modifier.sku
            const modifierQuantity = modifier.quantity
            const modifierItem = modifierItems.find(
              ({ id }) => id === modifier_id
            )
            const modifierName = modifierItem ? modifierItem.name : ''
            return {
              id: modifierSku ? modifierSku : modifierName,
              quantity: modifierQuantity
            }
          })
        : []

    const body = {
      category: 'Product',
      action: TrackableEvent.ProductAdded,
      label: product.name,
      value: quantity
    }

    trackUserActionsGA4(body, 'slerpGA4Tracking')

    // legacy tracking
    trackUserActionsFBPixel('AddToCart', {
      content_name: product.name,
      content_type: 'product',
      contents: [...contents, { id: chosenVariantId, quantity }]
    })
    trackUserActionsGA4(body, 'merchantGA4Tracking')
  }

  const addItemToCart = async () => {
    if (!cartParams) return
    const { appliedModifiers } = cartParams

    const formattedAppliedModifiers =
      createModifiersCartPayload(appliedModifiers)
    setCartLabel(labelWithSpinner('Adding to cart'))

    const { quantity, variantId } = cartParams

    await createOrderItemConsumerCart({
      quantity,
      variantId,
      modifiers: formattedAppliedModifiers,
      source: 'PRODUCT_MODAL'
    }).then((res: CartMutationResponse | null) => {
      if (!res) return
      const { cart, errors } = res
      if (errors?.length) {
        const currentProductError = findErrorWarning(
          errors,
          isCartProductError
        ) as CartProductError

        // return out if there is a CartProductError, they are handled more globally in ShopBody.tsx
        if (currentProductError) {
          setCartLabel('Add to cart')
          return
        }
      }

      const justAddedOrderItem = cart?.orderItems?.find(
        (item) => item.variantId === variantId
      )

      if (justAddedOrderItem?.id) {
        ecommTrackAddToCartCallback({
          variantId: justAddedOrderItem?.variantId,
          amount: Number(justAddedOrderItem.total.base),
          variantVat: 0,
          variantPrice: Number(justAddedOrderItem.variantPrice.base),
          quantity: justAddedOrderItem.quantity,
          storeName: consumerCart?.store.name || '',
          partnerName: partner?.name || ''
        })
        try {
          trackAddToCartCallback(
            justAddedOrderItem.quantity,
            formatModifiersParams(appliedModifiers)
          )
        } catch (error) {
          console.error(error)
        }
      }
      addToCartCallBack()
      closeModal('fulfillment')
    })
  }

  const {
    handleCreateDefaultCart,
    setShowCartSwitchModal,
    showCartSwitchModal
  } = useCreateDefaultCart(
    currentPartnerStore,
    queryParamFulfillmentType,
    addItemToCart
  )

  const addToCart = async () => {
    if (!cartParams) return
    if (!currentPartnerStore) return

    if (!optionsValid || !modifierGroupsValid) {
      handleFailedAddToCart()
      return
    }

    if (isCartMatch) {
      addItemToCart()
    }

    if (!cartId) {
      handleCreateDefaultCart()
      return
    }

    if (!isCartMatch && cartId) {
      // Only open cart switch modal if there are items in the cart
      if (consumerCart?.orderItems.length) {
        openModal('cartSwitch')
        setShowCartSwitchModal(true)
        return
      } else {
        handleCreateDefaultCart()
      }
    }
  }

  const cartActionLabel = () => {
    if (cartLabel !== 'Add to cart') {
      return <>{cartLabel}</>
    }

    const hasDiscount =
      !!cartParams?.amount.discounted &&
      cartParams?.amount.discounted !== cartParams?.amount.base
    return (
      <LabelContainer>
        <p>{cartLabel}&nbsp;</p>
        <Price>
          {hasDiscount
            ? formattedPrice.discountedPrice
            : formattedPrice.basePrice}
          {hasDiscount && (
            <StyledStrikethroughPrice>
              {formattedPrice.basePrice}
            </StyledStrikethroughPrice>
          )}
        </Price>
      </LabelContainer>
    )
  }

  const Price = styled.div({
    display: 'flex',
    flexDirection: 'column',
    fontSize: '14px',
    lineHeight: '14px'
  })

  const LabelContainer = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '> p': {
      margin: '0 24px 0 0'
    }
  })

  type ButtonState = {
    content: JSX.Element | string
    disabled: boolean
    state: 'loading' | 'available' | 'outofstock' | 'unavailable'
  }

  // Logic for Add to Cart button's text content and button state
  const getButtonState = (): ButtonState => {
    // return spinner if product is loading
    const outOfStockMessage = 'Out of stock'
    // return 'Out of stock' if product is out of stock
    if (!isProductInStock) {
      return {
        content: outOfStockMessage,
        disabled: true,
        state: 'outofstock'
      }
    }
    // return 'Currently Unavailable' if current store is not accepting orders
    if (!isStoreAcceptingOrders) {
      return {
        content: 'Currently Unavailable',
        disabled: true,
        state: 'unavailable'
      }
    }
    // return 'Out of stock' if all options have been selected and the variant is out of stock
    if (optionsValid && !isSelectedVariantInStock) {
      return {
        content: outOfStockMessage,
        disabled: true,
        state: 'outofstock'
      }
    }
    // otherwise return cart action with cost
    return {
      content: cartActionLabel(),
      disabled: false,
      state: 'available'
    }
  }

  return (
    <>
      {isModalOpen('cartSwitch') &&
        showCartSwitchModal &&
        cartSwitchModal({
          setShowCartSwitchModal,
          switchCartStore: handleCreateDefaultCart,
          storeName: currentPartnerStore?.name || '',
          cartFulfillmentDatetime: consumerCart?.fulfillment.window.from
        })}
      <Action data-testid='product-actions'>
        <Button
          testId='add-to-cart-button'
          onClick={addToCart}
          disabled={getButtonState().disabled}
          size='auto'
          borderRadius='6px'
          height={'44px'}
        >
          {getButtonState().content}
        </Button>
      </Action>
    </>
  )
}

const labelWithSpinner = (text: string) => {
  return (
    <>
      <Spinner />
      {text}
    </>
  )
}

const Action = styled.div(({ theme }) => {
  const themeSpace = theme.space ?? defaultTheme.space!
  return {
    alignSelf: 'center',
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
    [theme.mediaQueries.viewport7]: {
      position: 'unset',
      boxShadow: 'unset',
      padding: 'unset'
    },
    '& button': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: theme.fontSizes[1],
      lineHeight: '22px',
      padding: `${themeSpace[2]}px ${themeSpace[4]}px`,
      minWidth: '200px',
      width: 'unset'
    }
  }
})

export default Actions
